(() => {
  const roots = new WeakMap();
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const mobileMedia = matchMedia('(max-width: 720px)');
  const limitMedia = () => reduceMotion.matches || Boolean(navigator.connection?.saveData);
  const constrainedDevice = () => {
    const connection = navigator.connection;
    return /(^|-)2g$/.test(connection?.effectiveType || '') || Number(navigator.deviceMemory || 8) <= 4;
  };
  const clamp = value => Math.min(1, Math.max(0, value));
  const smooth = value => {
    const normalized = clamp(value);
    return normalized * normalized * (3 - 2 * normalized);
  };
  const within = (root, selector) => [
    ...(root.matches?.(selector) ? [root] : []),
    ...root.querySelectorAll(selector)
  ];

  const observeProgress = (stage, draw) => {
    let frame = 0;
    let visible = false;
    const schedule = () => {
      if (visible && !frame) frame = requestAnimationFrame(() => {
        frame = 0;
        draw();
      });
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) schedule();
      if (!visible && frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
    }, { threshold: 0.001 });
    observer.observe(stage);
    addEventListener('scroll', schedule, { passive: true });
    addEventListener('resize', schedule, { passive: true });
    draw();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
      removeEventListener('scroll', schedule);
      removeEventListener('resize', schedule);
    };
  };

  const initSystem = (stage, cleanups) => {
    const sticky = stage.querySelector('.rv-system__sticky');
    const video = stage.querySelector('video');
    const beats = [...stage.querySelectorAll('[data-rv-beat]')];
    const stageStarts = [0, 0.12, 0.34, 0.71];
    const mediaShift = [14, -14, 14, -14];
    const filmMap = [
      { scroll: 0, film: 0 },
      { scroll: 0.12, film: 0.065 },
      { scroll: 0.34, film: 0.3 },
      { scroll: 0.71, film: 0.74 },
      { scroll: 1, film: 1 }
    ];
    const mapFilm = progress => {
      const endIndex = filmMap.findIndex(point => point.scroll >= progress);
      if (endIndex <= 0) return filmMap[0].film;
      const start = filmMap[endIndex - 1];
      const end = filmMap[endIndex];
      const local = (progress - start.scroll) / (end.scroll - start.scroll);
      return start.film + local * (end.film - start.film);
    };
    let duration = 0;
    let pendingTime = 0;
    let painted = false;
    let objectUrl = '';
    let loadAbort = null;
    let seekTimer = 0;
    let lastSeek = 0;

    const revealPaintedFrame = () => {
      if (painted || video.readyState < 2) return;
      painted = true;
      stage.dataset.mediaState = 'ready';
    };

    const commitSeek = () => {
      seekTimer = 0;
      if (!duration || video.readyState < 1) return;
      if (Math.abs(video.currentTime - pendingTime) <= 1 / 48) return;
      lastSeek = performance.now();
      video.currentTime = pendingTime;
    };
    const seekLatest = () => {
      if (!duration || video.readyState < 1 || seekTimer) return;
      const wait = Math.max(0, 40 - (performance.now() - lastSeek));
      if (wait > 0) seekTimer = setTimeout(commitSeek, wait);
      else commitSeek();
    };

    const draw = () => {
      const rect = stage.getBoundingClientRect();
      const travel = Math.max(1, stage.offsetHeight - (sticky?.offsetHeight || innerHeight));
      const progress = clamp(-rect.top / travel);
      const state = stageStarts.reduce((active, start, index) => progress >= start ? index : active, 0);
      const edge = progress < 0.04 ? 1 - smooth(progress / 0.04) : 0;
      const release = smooth((progress - 0.88) / 0.12);
      stage.style.setProperty('--system-stage', String(state));
      stage.style.setProperty('--system-progress', progress.toFixed(4));
      stage.style.setProperty('--system-edge', edge.toFixed(3));
      stage.style.setProperty('--system-release', release.toFixed(4));
      stage.style.setProperty('--system-media-x', `${mediaShift[state]}vw`);
      stage.dataset.systemState = String(state);
      beats.forEach((beat, index) => beat.classList.toggle('is-active', index === state));

      if (video && duration > 0) {
        pendingTime = 0.001 + mapFilm(progress) * Math.max(0.001, duration - (1 / 24) - 0.001);
        seekLatest();
      }
    };

    if (!video || limitMedia()) {
      stage.dataset.mediaState = 'fallback';
      return;
    }

    const onMetadata = () => {
      duration = Number.isFinite(video.duration) ? video.duration : 0;
      if (!duration) stage.dataset.mediaState = 'fallback';
      draw();
    };
    const onLoadedData = () => {
      revealPaintedFrame();
      seekLatest();
    };
    const onSeeked = () => {
      revealPaintedFrame();
      seekLatest();
    };
    const onError = () => { stage.dataset.mediaState = 'fallback'; };
    const loadSource = () => {
      const compact = mobileMedia.matches || constrainedDevice();
      const source = compact ? (video.dataset.srcMobile || video.dataset.src) : video.dataset.src;
      if (!source || stage.dataset.mediaSource === source) return;
      loadAbort?.abort();
      loadAbort = new AbortController();
      duration = 0;
      painted = false;
      stage.dataset.mediaSource = source;
      stage.dataset.mediaState = 'loading';
      if (compact) {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        objectUrl = '';
        video.preload = 'auto';
        video.src = source;
        video.load();
        return;
      }
      fetch(source, { signal: loadAbort.signal, cache: 'force-cache' })
        .then(response => {
          if (!response.ok) throw Error(`Scrub film failed: ${response.status}`);
          return response.blob();
        })
        .then(blob => {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          objectUrl = URL.createObjectURL(blob);
          video.preload = 'auto';
          video.src = objectUrl;
          video.load();
        })
        .catch(error => {
          if (error.name !== 'AbortError') stage.dataset.mediaState = 'fallback';
        });
    };
    let sourceLoaded = false;
    const onViewportChange = () => {
      if (sourceLoaded) loadSource();
    };
    const sourceObserver = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      sourceLoaded = true;
      sourceObserver.disconnect();
      loadSource();
    }, { rootMargin: '175% 0px' });

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.pause();
    video.addEventListener('loadedmetadata', onMetadata);
    video.addEventListener('loadeddata', onLoadedData);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);
    mobileMedia.addEventListener('change', onViewportChange);
    sourceObserver.observe(stage);
    cleanups.push(
      observeProgress(stage, draw),
      () => sourceObserver.disconnect(),
      () => video.removeEventListener('loadedmetadata', onMetadata),
      () => video.removeEventListener('loadeddata', onLoadedData),
      () => video.removeEventListener('seeked', onSeeked),
      () => video.removeEventListener('error', onError),
      () => mobileMedia.removeEventListener('change', onViewportChange),
      () => {
        loadAbort?.abort();
        clearTimeout(seekTimer);
        video.removeAttribute('src');
        video.load();
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      }
    );
  };

  const initModels = (selector, cleanups) => {
    const tabs = [...selector.querySelectorAll('[data-rv-model]')];
    const panel = selector.querySelector('[data-rv-model-panel]');
    const image = panel?.querySelector('img');
    if (!panel || !image || tabs.length === 0) return;
    const choose = tab => {
      tabs.forEach(item => {
        const selected = item === tab;
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      let data;
      try { data = JSON.parse(tab.dataset.modelJson); }
      catch (error) { console.error('REVTOL model configuration is invalid.', error); return; }
      panel.setAttribute('aria-labelledby', tab.id);
      panel.querySelector('[data-role]').textContent = data.role;
      panel.querySelector('[data-name]').textContent = data.name;
      panel.querySelector('[data-specs]').innerHTML = data.specs.map(spec => `<div><dt>${spec[0]}</dt><dd>${spec[1]}</dd></div>`).join('');
      panel.querySelector('[data-note]').textContent = data.note || '';
      const productLink = panel.querySelector('[data-product-link]');
      if (productLink) {
        productLink.href = data.url;
        productLink.textContent = `Explore ${data.name}`;
      }
      const source = panel.querySelector('[data-rv-model-source]');
      if (source) source.srcset = data.imageMobile || data.image;
      image.src = data.image;
      image.alt = data.alt;
    };
    tabs.forEach((tab, index) => {
      const click = () => choose(tab);
      const keydown = event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const next = event.key === 'Home' ? 0
          : event.key === 'End' ? tabs.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        tabs[next].focus();
        choose(tabs[next]);
      };
      tab.addEventListener('click', click);
      tab.addEventListener('keydown', keydown);
      cleanups.push(() => {
        tab.removeEventListener('click', click);
        tab.removeEventListener('keydown', keydown);
      });
    });
  };

  const init = root => {
    if (roots.has(root)) return;
    const cleanups = [];
    root.dataset.rvInit = 'true';
    roots.set(root, () => {
      cleanups.splice(0).reverse().forEach(cleanup => cleanup());
      delete root.dataset.rvInit;
      roots.delete(root);
    });

    const heroVideo = root.querySelector('[data-rv-hero-video]');
    if (heroVideo && !limitMedia()) {
      const reveal = () => root.querySelector('.rv-hero')?.setAttribute('data-video-ready', 'true');
      heroVideo.addEventListener('playing', reveal, { once: true });
      if (!heroVideo.src && heroVideo.dataset.src) {
        heroVideo.src = heroVideo.dataset.src;
        heroVideo.load();
      }
      heroVideo.play().catch(() => {});
      cleanups.push(() => {
        heroVideo.removeEventListener('playing', reveal);
        heroVideo.pause();
        heroVideo.removeAttribute('src');
        heroVideo.load();
      });
    }

    within(root, '[data-rv-system]').forEach(stage => initSystem(stage, cleanups));
    within(root, '[data-rv-models]').forEach(selector => initModels(selector, cleanups));
  };

  const find = target => target.matches?.('[data-rv-page]') ? target : target.querySelector?.('[data-rv-page]');
  document.querySelectorAll('[data-rv-page]').forEach(init);
  const scrollToHash = hash => {
    if (!hash || hash === '#') return false;
    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!target) return false;
    target.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'start' });
    return true;
  };
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href^="#"]');
    if (!link || !scrollToHash(link.hash)) return;
    event.preventDefault();
    history.pushState(null, '', link.hash);
  });
  if (location.hash) addEventListener('load', () => requestAnimationFrame(() => scrollToHash(location.hash)), { once: true });
  document.addEventListener('shopify:section:load', event => {
    const root = find(event.target);
    if (root) init(root);
  });
  document.addEventListener('shopify:section:unload', event => {
    const root = find(event.target);
    if (root) roots.get(root)?.();
  });
})();
